import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { formatFilterDate } from 'src/app/pages/operation/shared/operation-filter.utils';
import { getAuthorizationDisplayName } from 'src/app/components/platform-header/platform-header-user.model';
import { MenuService } from 'src/app/components/menu/service/menu.service';
import { LoggedInService } from 'src/app/services/auth/loggedin.service';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { WorkingDaysService } from 'src/app/services/working-days/working-days.service';
import { ProjectService } from 'src/app/pages/projects-repository/services/project.service';
import { PositionConsultService } from 'src/app/pages/position/position-consult/services/position-consult.service';
import {
  HomeDashboardData,
  HomeDashboardOptions,
  HomeHeroSlide,
  HomeHeroStat,
} from '../models/home.models';

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  private readonly dashboardSubject = new BehaviorSubject<HomeDashboardData | null>(null);
  private readonly inFlightRequests = new Map<string, Observable<HomeDashboardData>>();

  readonly dashboard$ = this.dashboardSubject.asObservable();

  constructor(
    private readonly loggedInService: LoggedInService,
    private readonly menuService: MenuService,
    private readonly projectService: ProjectService,
    private readonly positionConsultService: PositionConsultService,
    private readonly operationTransferenceService: OperationTransferenceService,
    private readonly workingDaysService: WorkingDaysService,
  ) {}

  hasDashboardData(): boolean {
    return this.dashboardSubject.getValue() !== null;
  }

  loadDashboard(options: HomeDashboardOptions, refresh = false): Observable<HomeDashboardData> {
    const cacheKey = this.buildCacheKey(options);

    if (refresh) {
      this.inFlightRequests.delete(cacheKey);
    }

    const inFlight = this.inFlightRequests.get(cacheKey);

    if (inFlight && !refresh) {
      return inFlight;
    }

    const request$ = this.fetchDashboard(options).pipe(
      tap(data => this.dashboardSubject.next(data)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inFlightRequests.delete(cacheKey)),
    );

    this.inFlightRequests.set(cacheKey, request$);

    return request$;
  }

  buildGreetingMessage(userName: string, balanceChanged = false, showCreditStock = true): string {
    const firstName = userName.trim().split(/\s+/).filter(Boolean)[0] ?? 'usuário';

    if (!showCreditStock) {
      return `Olá ${firstName}, bem-vindo!`;
    }

    const variation = balanceChanged ? 'variou' : 'não variou';

    return `Olá ${firstName}, seu saldo de créditos ${variation} nos últimos 30 dias!`;
  }

  private buildCacheKey(options: HomeDashboardOptions): string {
    return `${options.showCreditStock}:${options.showRegisteredProjects}`;
  }

  private fetchDashboard(options: HomeDashboardOptions): Observable<HomeDashboardData> {
    const { showCreditStock, showRegisteredProjects } = options;

    return forkJoin({
      creditStock: showCreditStock ? this.getCreditStockTotal() : of(null),
      projectsCount: showRegisteredProjects ? this.getRegisteredProjectsCount() : of(null),
      creditBalanceChanged: showCreditStock ? this.hasCreditBalanceChangedInLast30Days() : of(false),
    }).pipe(
      map(({ creditStock, projectsCount, creditBalanceChanged }) =>
        this.buildDashboardData(creditStock, projectsCount, options, creditBalanceChanged),
      ),
    );
  }

  private getCreditStockTotal(): Observable<number> {
    return this.positionConsultService.getCustodyTotal().pipe(
      catchError(() => of(0)),
    );
  }

  private getRegisteredProjectsCount(): Observable<number> {
    return this.projectService.getProjects({}, { page: 1, size: 1, results: [], total: 0 }).pipe(
      map(response => response.meta?.totalRecords ?? 0),
      catchError(() => of(0)),
    );
  }

  private hasCreditBalanceChangedInLast30Days(): Observable<boolean> {
    return this.getLast30DaysOperationFilterDates().pipe(
      switchMap(({ initialDate, finalDate }) =>
        this.operationTransferenceService.hasOperationsInPeriod(initialDate, finalDate),
      ),
      catchError(() => of(false)),
    );
  }

  private getLast30DaysOperationFilterDates(): Observable<{ initialDate: string; finalDate: string }> {
    const today = new Date();
    const finalDate = formatFilterDate(today)!;

    return this.workingDaysService.getWorkingDays(today, 60).pipe(
      map(workingDays => ({
        initialDate: this.getInitialDateFromWorkingDays(workingDays, today),
        finalDate,
      })),
      catchError(() =>
        of({
          initialDate: this.getCalendarDaysAgo(today, 30),
          finalDate,
        }),
      ),
    );
  }

  private getInitialDateFromWorkingDays(workingDays: string[], today: Date): string {
    const thirtiethWorkingDay = workingDays[29];

    if (!thirtiethWorkingDay) {
      return this.getCalendarDaysAgo(today, 30);
    }

    const initialDate = new Date(`${thirtiethWorkingDay}T00:00:00`);
    initialDate.setDate(initialDate.getDate() + 1);

    return formatFilterDate(initialDate) ?? this.getCalendarDaysAgo(today, 30);
  }

  private getCalendarDaysAgo(date: Date, days: number): string {
    const result = new Date(date);
    result.setDate(result.getDate() - days);

    return formatFilterDate(result)!;
  }

  private buildDashboardData(
    creditStock: number | null,
    registeredProjects: number | null,
    options: HomeDashboardOptions,
    creditBalanceChanged: boolean,
  ): HomeDashboardData {
    const stats = this.buildStats(creditStock, registeredProjects, options);

    return {
      greetingMessage: this.buildGreetingMessage(
        this.getUserName(),
        creditBalanceChanged,
        options.showCreditStock,
      ),
      slides: this.buildSlides(stats),
    };
  }

  private buildStats(
    creditStock: number | null,
    registeredProjects: number | null,
    options: HomeDashboardOptions,
  ): HomeHeroStat[] {
    const stats: HomeHeroStat[] = [];

    if (options.showCreditStock) {
      stats.push({
        label: 'Estoque de Créditos',
        value: this.formatNumber(creditStock ?? 0),
        unit: 'tCO₂e',
      });
    }

    if (options.showRegisteredProjects) {
      stats.push({
        label: 'Projetos registrados',
        value: this.formatNumber(registeredProjects ?? 0),
        unit: 'Projetos',
      });
    }

    return stats;
  }

  private buildSlides(stats: HomeHeroStat[]): HomeHeroSlide[] {
    return [
      {
        id: 'carbon-credits',
        badge: 'Painel Principal',
        title: 'Créditos de Carbono',
        description:
          'Registre projetos, emita e transfira créditos de carbono com validação simplificada e conformidade internacional.',
        stats,
      },
    ];
  }

  private getUserName(): string {
    const profileName = getAuthorizationDisplayName(this.menuService.userProfile$.getValue());

    if (profileName) {
      return profileName;
    }

    return this.loggedInService.tokenContent$.getValue()?.nomeUsuario?.trim() ?? '';
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('pt-BR').format(value);
  }
}
